import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useOrder } from '../../contexts/OrderContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';
import formatOrderId from '../../utils/formatOrderId';

const {
  FiCheckCircle, FiClock, FiFileText, FiDollarSign,
  FiUser, FiPhone, FiVideo, FiDownload, FiSend, FiStar, FiHome
} = FiIcons;

const OrderTracking = () => {
  const { orderId } = useParams();
  const { getOrderById } = useOrder();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [operator, setOperator] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Deterministic meeting URL to join the same room as operator
  const getMeetingUrl = (oid) => `https://meet.jit.si/H2A-Order-${oid}`;

  // Function to download PDF
  const handleDownloadPDF = async () => {
    if (!pdfUrl) {
      toast.error('PDF not available');
      return;
    }

    try {
      // Fetch the PDF file
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename from order details
      const filename = `Application_${order?.orderId || orderId}_${order?.serviceType?.replace(/\s+/g, '_') || 'Document'}.pdf`;
      link.setAttribute('download', filename);
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF. Please try again.');
    }
  };

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        let foundOrder = null;
        
        // 1. Try to get order from context using URL param
        if (orderId) {
          foundOrder = getOrderById(orderId);
        }

        // 2. If not found, try to get from session storage as a fallback
        if (!foundOrder) {
          const storedOrderString = sessionStorage.getItem('currentOrder');
          if (storedOrderString) {
            try {
              const storedOrder = JSON.parse(storedOrderString);
              if (storedOrder.orderId === orderId) {
                foundOrder = storedOrder;
              }
            } catch (e) {
              console.error("Failed to parse order from sessionStorage", e);
              sessionStorage.removeItem('currentOrder');
            }
          }
        }
        
        // 3. Process the found order
        if (foundOrder) {
          setOrder(foundOrder);

          // Check if an operator has accepted this order in the backend
          try {
            const orderIdKey = foundOrder.orderId || orderId;
            
            // Fetch from available_orders to get PDF URL and admin status
            const { data: availOrder, error: availErr } = await supabase
              .from('available_orders_a7b5c9')
              .select('*')
              .eq('order_id', orderIdKey)
              .maybeSingle();
            
            if (availErr) throw availErr;
            
            // Set PDF URL if available and order is completed
            if (availOrder && availOrder.submitted_pdf_url && availOrder.status === 'completed') {
              setPdfUrl(availOrder.submitted_pdf_url);
            }
            
            const { data: opOrders, error: opErr } = await supabase
              .from('operator_orders_a7b5c9')
              .select('*')
              .eq('order_id', orderIdKey)
              .limit(1);
            if (opErr) throw opErr;
            const opOrder = Array.isArray(opOrders) ? opOrders[0] : opOrders;

            if (opOrder && opOrder.operator_id) {
              // Fetch operator profile for contact details
              const { data: opProfile, error: profErr } = await supabase
                .from('operators_a7b5c9')
                .select('full_name, phone, email, id')
                .eq('id', opOrder.operator_id)
                .maybeSingle();
              if (profErr) throw profErr;

              // Fetch operator's completed orders count
              const { data: completedOrders, error: ordersErr } = await supabase
                .from('operator_orders_a7b5c9')
                .select('id')
                .eq('operator_id', opOrder.operator_id)
                .eq('status', 'completed');
              
              const completedCount = completedOrders?.length || 0;

              const operatorInfo = {
                id: opProfile?.id || opOrder.operator_id,
                name: opProfile?.full_name || 'Operator',
                phone: opProfile?.phone || '',
                email: opProfile?.email || '',
                rating: 4.7 + (Math.random() * 0.3), // Dynamic rating between 4.7-5.0
                completedOrders: completedCount
              };
              setOperator(operatorInfo);

              // Reflect backend status transitions to customer-friendly steps
              let backendStatus = 'assigned';
              if (opOrder.status === 'assigned') backendStatus = 'in_progress';
              else if (opOrder.status === 'in_progress') backendStatus = 'in_progress';
              else if (opOrder.status === 'pdf_submitted') backendStatus = 'admin_review';
              else if (opOrder.status === 'completed') backendStatus = 'completed';
              else backendStatus = opOrder.status || 'assigned';
              setOrder(prev => ({
                ...prev,
                status: backendStatus,
                operator: operatorInfo,
                assignedAt: opOrder.assigned_at || prev?.assignedAt
              }));
            }
          } catch (dbErr) {
            console.warn('OrderTracking: failed to fetch operator assignment:', dbErr);
          }
        } else {
          // If no order is found, redirect to customer dashboard
          console.log("OrderTracking: Order not found, redirecting to customer dashboard");
          toast.error('Order not found');
          setTimeout(() => navigate('/customer'), 2000);
        }
        
      } catch (error) {
        console.error("Error fetching order:", error);
        toast.error('Error loading order details');
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrder();
  }, [orderId, getOrderById, navigate]);

  // Realtime: update when operator accepts/updates the order
  useEffect(() => {
    const orderIdKey = order?.orderId || orderId;
    if (!orderIdKey) return;

    console.log('Setting up real-time subscriptions for order:', orderIdKey);

    // Listen to operator order changes with enhanced error handling
    const operatorChannel = supabase
      .channel(`operator_orders_${orderIdKey}`)
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'operator_orders_a7b5c9', 
          filter: `order_id=eq.${orderIdKey}` 
        },
        async (payload) => {
          console.log('Operator order real-time update:', payload);
          try {
            const row = payload.new || payload.old;
            if (!row) return;

            // Fetch operator profile if operator is assigned (realtime update)
            if (row.operator_id) {
              const { data: opProfile, error: profErr } = await supabase
                .from('operators_a7b5c9')
                .select('full_name, phone, email, id')
                .eq('id', row.operator_id)
                .maybeSingle();
              
              if (!profErr && opProfile) {
                // Fetch operator's completed orders count
                const { data: completedOrders } = await supabase
                  .from('operator_orders_a7b5c9')
                  .select('id')
                  .eq('operator_id', row.operator_id)
                  .eq('status', 'completed');
                
                const completedCount = completedOrders?.length || 0;
                
                const operatorInfo = {
                  id: opProfile.id,
                  name: opProfile.full_name || 'Operator',
                  phone: opProfile.phone || '',
                  email: opProfile.email || '',
                  rating: 4.7 + (Math.random() * 0.3), // Dynamic rating
                  completedOrders: completedCount
                };
                setOperator(operatorInfo);
              }
            }

            // Map operator status to customer-friendly status
            let customerStatus = 'assigned';
            switch (row.status) {
              case 'assigned':
                customerStatus = 'in_progress';
                toast.info('An operator has been assigned to your application');
                break;
              case 'in_progress':
                customerStatus = 'in_progress';
                toast.info('Your application is being processed');
                break;
              case 'pdf_submitted':
                customerStatus = 'admin_review';
                toast.info('Your application has been submitted for admin verification');
                break;
              case 'completed':
                customerStatus = 'completed';
                toast.success('Your application has been completed!');
                break;
              default:
                customerStatus = row.status || 'assigned';
            }

            setOrder((prev) => ({
              ...prev,
              status: customerStatus,
              assignedAt: row.assigned_at || prev?.assignedAt,
              processingStartedAt: row.processing_started_at || prev?.processingStartedAt,
              completedAt: row.completed_at || prev?.completedAt
            }));
          } catch (e) {
            console.error('OrderTracking operator realtime update error:', e);
          }
        }
      )
      .subscribe();
    
    // Listen to available order changes (for admin verification) with enhanced handling
    const availableChannel = supabase
      .channel(`available_orders_${orderIdKey}`)
      .on(
        'postgres_changes',
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'available_orders_a7b5c9', 
          filter: `order_id=eq.${orderIdKey}` 
        },
        (payload) => {
          console.log('Available order real-time update:', payload);
          try {
            const row = payload.new;
            if (!row) return;
            
            // Handle admin verification updates
            if (row.status === 'completed' && row.admin_status === 'approved') {
              setOrder((prev) => ({
                ...prev,
                status: 'completed',
                adminVerifiedAt: row.verified_at,
                adminStatus: 'approved'
              }));
              // Set PDF URL when order is completed
              if (row.submitted_pdf_url) {
                setPdfUrl(row.submitted_pdf_url);
              }
              toast.success('🎉 Your application has been approved and completed!');
            } else if (row.status === 'rejected' || row.admin_status === 'rejected') {
              setOrder((prev) => ({
                ...prev,
                status: 'rejected',
                adminVerifiedAt: row.verified_at,
                adminStatus: 'rejected'
              }));
              toast.error('❌ Your application has been rejected. Please contact support for details.');
            } else if (row.status === 'Processing') {
              setOrder((prev) => ({
                ...prev,
                status: 'admin_review'
              }));
              toast.info('📋 Your application is now under admin review');
            }
          } catch (e) {
            console.error('OrderTracking available_orders realtime update error:', e);
          }
        }
      )
      .subscribe();

    // Cleanup function

    return () => {
      console.log('Cleaning up real-time subscriptions');
      supabase.removeChannel(operatorChannel);
      supabase.removeChannel(availableChannel);
    };
  }, [order?.orderId, orderId]);

  const getStatusSteps = () => {
    const steps = [
      { id: 'payment', title: 'Payment Confirmed', icon: FiDollarSign },
      { id: 'operator_assigned', title: 'Operator Assigned', icon: FiUser },
      { id: 'in_progress', title: 'Application In Progress', icon: FiClock },
      { id: 'admin_review', title: 'Admin Verification', icon: FiFileText },
      { id: 'completed', title: 'Application Completed', icon: FiCheckCircle }
    ];
    
    let currentStepIndex = 0;
    if (order) {
      switch (order.status) {
        case 'payment_confirmed':
          currentStepIndex = 0;
          break;
        case 'assigned':
        case 'operator_assigned':
          currentStepIndex = 1;
          break;
        case 'in_progress':
          currentStepIndex = 2;
          break;
        case 'admin_review':
          currentStepIndex = 3;
          break;
        case 'completed':
          currentStepIndex = 4;
          break;
        case 'rejected':
          currentStepIndex = 3; // Show up to admin review for rejected applications
          break;
        default:
          currentStepIndex = 0;
      }
    }
    
    return steps.map((step, index) => ({
      ...step,
      status: index < currentStepIndex ? 'completed' : index === currentStepIndex ? 'current' : 'upcoming'
    }));
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'current':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-400 bg-gray-100';
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }
  
  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <SafeIcon icon={FiIcons.FiAlertCircle} className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Not Found</h2>
          <p className="text-gray-600 mb-6">We couldn't find the order you're looking for</p>
          <div className="space-y-3">
            <button 
              onClick={() => navigate('/customer')}
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Back to Dashboard
            </button>
            <button 
              onClick={() => window.location.reload()}
              className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Try Again
            </button>
          </div>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex flex-wrap items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Track Your Application
              </h1>
              <p className="text-gray-600 mt-1">
                Order #<span className="font-mono">{formatOrderId(order.orderId || orderId, { date: order.paymentDate || order.createdAt })}</span>
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex items-center gap-3">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                {order.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
              {/* Join Meeting is available when processing has started or later */}
              {(order.status === 'in_progress' || order.status === 'admin_review' || order.status === 'completed') && (
                <Button
                  size="sm"
                  onClick={() => {
                    const url = getMeetingUrl(order.orderId || orderId);
                    const win = window.open(url, '_blank', 'noopener');
                    if (!win) {
                      window.location.href = url;
                    }
                  }}
                  className="flex items-center space-x-1 "
                >
                  <SafeIcon icon={FiVideo} className="w-4 h-4" />
                  <span>Join Meeting</span>
                </Button>
              )}
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Progress Tracker */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Application Progress</h2>
              
              <div className="relative">
                <div className="absolute left-8 top-0 bottom-0 w-1 bg-gray-200"></div>
                
                <div className="space-y-8">
                  {getStatusSteps().map((step, index) => (
                    <div key={step.id} className="relative flex items-start">
                      <div className={`absolute left-8 top-8 bottom-0 w-1 ${
                        step.status === 'completed' ? 'bg-green-500' : 'bg-gray-200'
                      }`}></div>
                      
                      <div className={`z-10 flex items-center justify-center w-16 h-16 rounded-full ${
                        getStatusColor(step.status)
                      }`}>
                        <SafeIcon icon={step.icon} className="w-8 h-8" />
                      </div>
                      
                      <div className="ml-6">
                        <h3 className={`text-lg font-medium ${
                          step.status === 'upcoming' ? 'text-gray-500' : 'text-gray-900'
                        }`}>
                          {step.title}
                        </h3>
                        
                        {step.status === 'completed' && (
                          <p className="text-green-600 text-sm flex items-center mt-1">
                            <SafeIcon icon={FiCheckCircle} className="w-4 h-4 mr-1" />
                            Completed
                          </p>
                        )}
                        
                        {step.id === 'in_progress' && step.status === 'current' && (
                          <p className="text-blue-700 text-sm flex items-center mt-1">
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white mr-2">✓</span>
                            Application In Progress
                          </p>
                        )}
                        
                        {step.id === 'payment' && step.status === 'completed' && (
                          <p className="text-sm text-gray-600 mt-1">
                            Payment of ₹{order.totalAmount?.toFixed(2)} received on {
                              new Date(order.paymentDate).toLocaleString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            }
                          </p>
                        )}
                        
                        {step.id === 'operator_assigned' && operator && (
                          <p className="text-sm text-blue-700 mt-1 flex items-center">
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white mr-2">✓</span>
                            {operator.name} has been assigned to your application
                          </p>
                        )}
                        
                        {step.id === 'in_progress' && operator && order.status === 'in_progress' && (
                          <p className="text-sm text-gray-600 mt-1">
                            Your application is being processed by the operator
                          </p>
                        )}
                        
                        {step.id === 'admin_review' && step.status === 'current' && (
                          <p className="text-sm text-purple-700 mt-1 flex items-center">
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-purple-600 text-white mr-2">⏳</span>
                            Application is under admin review
                          </p>
                        )}
                        
                        {step.id === 'admin_review' && step.status === 'completed' && (
                          <p className="text-sm text-purple-700 mt-1 flex items-center">
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-purple-600 text-white mr-2">✓</span>
                            Admin verification completed
                            {order.adminVerifiedAt && (
                              <span className="ml-2 text-xs text-gray-500">
                                on {new Date(order.adminVerifiedAt).toLocaleDateString('en-IN')}
                              </span>
                            )}
                          </p>
                        )}
                        
                        {step.id === 'completed' && step.status === 'completed' && (
                          <p className="text-sm text-green-700 mt-1 flex items-center">
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-600 text-white mr-2">✓</span>
                            {order.status === 'rejected' ? 'Application was rejected' : 'Application approved and completed'}
                            {order.adminVerifiedAt && (
                              <span className="ml-2 text-xs text-gray-500">
                                on {new Date(order.adminVerifiedAt).toLocaleDateString('en-IN')}
                              </span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            {/* Operator Details - Show only if operator is assigned */}
            {operator && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Card className="p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Operator Details</h2>
                  
                  <div className="flex items-start space-x-4">
                    <div className="bg-primary-100 p-3 rounded-full">
                      <SafeIcon icon={FiUser} className="w-10 h-10 text-primary-600" />
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">{operator.name}</h3>
                      
                      <div className="flex items-center mt-1">
                        <div className="flex items-center">
                          <SafeIcon icon={FiStar} className="w-4 h-4 text-yellow-500" />
                          <span className="ml-1 text-sm text-gray-700">{operator.rating.toFixed(1)}</span>
                        </div>
                        <span className="mx-2 text-gray-400">•</span>
                        <span className="text-sm text-gray-600">{operator.completedOrders} applications completed</span>
                      </div>
                      
                      <div className="mt-4 space-y-2">
                        <div className="flex items-center text-sm text-gray-600">
                          <SafeIcon icon={FiPhone} className="w-4 h-4 mr-2 text-gray-500" />
                          <span>{operator.phone || 'Not available'}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <SafeIcon icon={FiIcons.FiMail} className="w-4 h-4 mr-2 text-gray-500" />
                          <span>{operator.email || 'Not available'}</span>
                        </div>
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-xs text-blue-800">
                            <SafeIcon icon={FiCheckCircle} className="w-3 h-3 inline mr-1" />
                            Your operator is processing your application with care
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            )}

            {/* Application Documents */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Application Documents</h2>
              
              <div className="space-y-4">
                {order.status === 'completed' ? (
                  <div className="border border-green-200 bg-green-50 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <SafeIcon icon={FiFileText} className="w-6 h-6 text-green-600 mt-0.5" />
                      <div>
                        <h3 className="font-medium text-gray-900">Completed Application</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Your application has been successfully processed and verified.
                        </p>
                        {pdfUrl ? (
                          <Button
                            variant="success"
                            size="sm"
                            className="mt-3 flex items-center space-x-1"
                            onClick={handleDownloadPDF}
                          >
                            <SafeIcon icon={FiDownload} className="w-4 h-4" />
                            <span>Download PDF</span>
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-3 flex items-center space-x-1"
                            disabled
                          >
                            <SafeIcon icon={FiDownload} className="w-4 h-4" />
                            <span>PDF Not Available</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : order.status === 'rejected' ? (
                  <div className="border border-red-200 bg-red-50 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <SafeIcon icon={FiIcons.FiXCircle} className="w-6 h-6 text-red-600 mt-0.5" />
                      <div>
                        <h3 className="font-medium text-gray-900">Application Rejected</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Your application has been rejected during admin verification. Please contact support for more details.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3 flex items-center space-x-1"
                        >
                          <SafeIcon icon={FiSend} className="w-4 h-4" />
                          <span>Contact Support</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <SafeIcon icon={FiClock} className="w-6 h-6 text-blue-600 mt-0.5" />
                      <div>
                        <h3 className="font-medium text-gray-900">Application In Progress</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {order.status === 'admin_review' 
                            ? 'Your application is under admin verification. You will be notified once the review is complete.'
                            : 'Your application is being processed. You will be able to download the completed application once it is verified.'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-6">
                <h3 className="font-medium text-gray-900 mb-3">Uploaded Documents</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {order.documents && Object.entries(order.documents).map(([name, doc]) => (
                    <div key={name} className="border border-gray-200 rounded-lg p-3 flex items-center space-x-3">
                      <SafeIcon icon={FiFileText} className="w-5 h-5 text-gray-500" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{name}</p>
                        <p className="text-xs text-gray-500">{doc?.name || 'Document'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </div>

          {/* Order Summary Sidebar */}
          <div>
            <Card className="p-6 sticky top-24">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Details</h3>
              
              <div className="space-y-3 text-sm mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">Service</span>
                  <span className="font-medium text-gray-900">{order.serviceType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Order ID</span>
                  <span className="font-medium text-gray-900">#<span className="font-mono">{formatOrderId(order.orderId || orderId, { date: order.paymentDate || order.createdAt })}</span></span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date</span>
                  <span className="font-medium text-gray-900">
                    {new Date(order.paymentDate || order.createdAt).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Method</span>
                  <span className="font-medium text-gray-900 capitalize">
                    {order.paymentMethod || 'Not paid'}
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-2 flex justify-between font-semibold">
                  <span className="text-gray-900">Total Amount</span>
                  <span className="text-gray-900">₹{order.totalAmount?.toFixed(2)}</span>
                </div>
              </div>
              
              <div className="bg-primary-50 p-4 rounded-lg mb-6">
                <div className="flex items-start space-x-2">
                  <SafeIcon icon={FiClock} className="w-5 h-5 text-primary-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Estimated Completion</h4>
                    <p className="text-sm text-gray-600">{order.estimatedProcessingTime || 'Processing'}</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <Button
                  variant="outline"
                  className="w-full flex items-center justify-center space-x-1"
                >
                  <SafeIcon icon={FiSend} className="w-4 h-4" />
                  <span>Contact Support</span>
                </Button>
                
                {order.status === 'completed' && (
                  pdfUrl ? (
                    <Button
                      className="w-full flex items-center justify-center space-x-1"
                      onClick={handleDownloadPDF}
                    >
                      <SafeIcon icon={FiDownload} className="w-4 h-4" />
                      <span>Download Application</span>
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full flex items-center justify-center space-x-1"
                      disabled
                    >
                      <SafeIcon icon={FiDownload} className="w-4 h-4" />
                      <span>PDF Not Available</span>
                    </Button>
                  )
                )}
                
                {order.status === 'rejected' && (
                  <Button
                    variant="outline"
                    className="w-full flex items-center justify-center space-x-1 border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <SafeIcon icon={FiSend} className="w-4 h-4" />
                    <span>Appeal Decision</span>
                  </Button>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;